/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AcceptInviteResultDto,
  ApiErrorDto,
  CreateInvitesDto,
  CreateInvitesResultDto,
  InviteDto,
  InvitePreviewDto,
  MemberDto,
  UpdateMemberRoleDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Everyone in the workspace, oldest first.
 */
export const listMembers = (id: string, signal?: AbortSignal) => {
  return customInstance<MemberDto[]>({
    url: `/api/v1/workspaces/${id}/members`,
    method: 'GET',
    signal,
  });
};

export const getListMembersQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/members`] as const;
};

export const getListMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof listMembers>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMembers>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMembersQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMembers>>> = ({ signal }) =>
    listMembers(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listMembers>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListMembersQueryResult = NonNullable<Awaited<ReturnType<typeof listMembers>>>;
export type ListMembersQueryError = ErrorType<ApiErrorDto>;

export function useListMembers<
  TData = Awaited<ReturnType<typeof listMembers>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMembers>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMembers>>,
          TError,
          Awaited<ReturnType<typeof listMembers>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMembers<
  TData = Awaited<ReturnType<typeof listMembers>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMembers>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMembers>>,
          TError,
          Awaited<ReturnType<typeof listMembers>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMembers<
  TData = Awaited<ReturnType<typeof listMembers>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMembers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Everyone in the workspace, oldest first.
 */

export function useListMembers<
  TData = Awaited<ReturnType<typeof listMembers>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMembers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListMembersQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Change someone's role. Owners only.
 */
export const updateMemberRole = (
  id: string,
  userId: string,
  updateMemberRoleDto: BodyType<UpdateMemberRoleDto>,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/members/${userId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateMemberRoleDto,
    signal,
  });
};

export const getUpdateMemberRoleMutationKey = () => ['updateMemberRole'] as const;

export const getUpdateMemberRoleMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMemberRole>>,
    TError,
    UpdateMemberRoleMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMemberRole>>,
  TError,
  UpdateMemberRoleMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateMemberRoleMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMemberRole>>,
    UpdateMemberRoleMutationVariables
  > = (props) => {
    const { id, userId, data } = props ?? {};

    return updateMemberRole(id, userId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMemberRoleMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMemberRole>>
>;
export type UpdateMemberRoleMutationBody = BodyType<UpdateMemberRoleDto>;
export type UpdateMemberRoleMutationError = ErrorType<ApiErrorDto>;
export type UpdateMemberRoleMutationVariables = {
  id: string;
  userId: string;
  data: BodyType<UpdateMemberRoleDto>;
};

/**
 * @summary Change someone's role. Owners only.
 */
export const useUpdateMemberRole = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateMemberRole>>,
      TError,
      UpdateMemberRoleMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateMemberRole>>,
  TError,
  UpdateMemberRoleMutationVariables,
  TContext
> => {
  return useMutation(getUpdateMemberRoleMutationOptions(options), queryClient);
};
/**
 * @summary Remove someone (owners), or leave (yourself).
 */
export const removeMember = (id: string, userId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/members/${userId}`,
    method: 'DELETE',
    signal,
  });
};

export const getRemoveMemberMutationKey = () => ['removeMember'] as const;

export const getRemoveMemberMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeMember>>,
    TError,
    RemoveMemberMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeMember>>,
  TError,
  RemoveMemberMutationVariables,
  TContext
> => {
  const mutationKey = getRemoveMemberMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeMember>>,
    RemoveMemberMutationVariables
  > = (props) => {
    const { id, userId } = props ?? {};

    return removeMember(id, userId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeMember>>
>;

export type RemoveMemberMutationError = ErrorType<ApiErrorDto>;
export type RemoveMemberMutationVariables = { id: string; userId: string };

/**
 * @summary Remove someone (owners), or leave (yourself).
 */
export const useRemoveMember = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removeMember>>,
      TError,
      RemoveMemberMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removeMember>>,
  TError,
  RemoveMemberMutationVariables,
  TContext
> => {
  return useMutation(getRemoveMemberMutationOptions(options), queryClient);
};
/**
 * @summary Invites waiting to be accepted.
 */
export const listInvites = (id: string, signal?: AbortSignal) => {
  return customInstance<InviteDto[]>({
    url: `/api/v1/workspaces/${id}/invites`,
    method: 'GET',
    signal,
  });
};

export const getListInvitesQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/invites`] as const;
};

export const getListInvitesQueryOptions = <
  TData = Awaited<ReturnType<typeof listInvites>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listInvites>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListInvitesQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listInvites>>> = ({ signal }) =>
    listInvites(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listInvites>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListInvitesQueryResult = NonNullable<Awaited<ReturnType<typeof listInvites>>>;
export type ListInvitesQueryError = ErrorType<ApiErrorDto>;

export function useListInvites<
  TData = Awaited<ReturnType<typeof listInvites>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listInvites>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInvites>>,
          TError,
          Awaited<ReturnType<typeof listInvites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListInvites<
  TData = Awaited<ReturnType<typeof listInvites>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listInvites>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInvites>>,
          TError,
          Awaited<ReturnType<typeof listInvites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListInvites<
  TData = Awaited<ReturnType<typeof listInvites>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Invites waiting to be accepted.
 */

export function useListInvites<
  TData = Awaited<ReturnType<typeof listInvites>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListInvitesQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Email invites to join as an editor or reviewer. Owners only, within the plan's seats.
 */
export const createInvites = (
  id: string,
  createInvitesDto: BodyType<CreateInvitesDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CreateInvitesResultDto>({
    url: `/api/v1/workspaces/${id}/invites`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createInvitesDto,
    signal,
  });
};

export const getCreateInvitesMutationKey = () => ['createInvites'] as const;

export const getCreateInvitesMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createInvites>>,
    TError,
    CreateInvitesMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createInvites>>,
  TError,
  CreateInvitesMutationVariables,
  TContext
> => {
  const mutationKey = getCreateInvitesMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createInvites>>,
    CreateInvitesMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createInvites(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateInvitesMutationResult = NonNullable<
  Awaited<ReturnType<typeof createInvites>>
>;
export type CreateInvitesMutationBody = BodyType<CreateInvitesDto>;
export type CreateInvitesMutationError = ErrorType<ApiErrorDto>;
export type CreateInvitesMutationVariables = {
  id: string;
  data: BodyType<CreateInvitesDto>;
};

/**
 * @summary Email invites to join as an editor or reviewer. Owners only, within the plan's seats.
 */
export const useCreateInvites = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createInvites>>,
      TError,
      CreateInvitesMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createInvites>>,
  TError,
  CreateInvitesMutationVariables,
  TContext
> => {
  return useMutation(getCreateInvitesMutationOptions(options), queryClient);
};
/**
 * @summary Send the invite again with a fresh link and 7 more days. Owners only.
 */
export const resendInvite = (id: string, inviteId: string, signal?: AbortSignal) => {
  return customInstance<InviteDto>({
    url: `/api/v1/workspaces/${id}/invites/${inviteId}/resend`,
    method: 'POST',
    signal,
  });
};

export const getResendInviteMutationKey = () => ['resendInvite'] as const;

export const getResendInviteMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resendInvite>>,
    TError,
    ResendInviteMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resendInvite>>,
  TError,
  ResendInviteMutationVariables,
  TContext
> => {
  const mutationKey = getResendInviteMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resendInvite>>,
    ResendInviteMutationVariables
  > = (props) => {
    const { id, inviteId } = props ?? {};

    return resendInvite(id, inviteId);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResendInviteMutationResult = NonNullable<
  Awaited<ReturnType<typeof resendInvite>>
>;

export type ResendInviteMutationError = ErrorType<ApiErrorDto>;
export type ResendInviteMutationVariables = { id: string; inviteId: string };

/**
 * @summary Send the invite again with a fresh link and 7 more days. Owners only.
 */
export const useResendInvite = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resendInvite>>,
      TError,
      ResendInviteMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof resendInvite>>,
  TError,
  ResendInviteMutationVariables,
  TContext
> => {
  return useMutation(getResendInviteMutationOptions(options), queryClient);
};
/**
 * @summary Cancel an invite; its link stops working. Owners only.
 */
export const revokeInvite = (id: string, inviteId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/invites/${inviteId}`,
    method: 'DELETE',
    signal,
  });
};

export const getRevokeInviteMutationKey = () => ['revokeInvite'] as const;

export const getRevokeInviteMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeInvite>>,
    TError,
    RevokeInviteMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeInvite>>,
  TError,
  RevokeInviteMutationVariables,
  TContext
> => {
  const mutationKey = getRevokeInviteMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeInvite>>,
    RevokeInviteMutationVariables
  > = (props) => {
    const { id, inviteId } = props ?? {};

    return revokeInvite(id, inviteId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeInviteMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeInvite>>
>;

export type RevokeInviteMutationError = ErrorType<ApiErrorDto>;
export type RevokeInviteMutationVariables = { id: string; inviteId: string };

/**
 * @summary Cancel an invite; its link stops working. Owners only.
 */
export const useRevokeInvite = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeInvite>>,
      TError,
      RevokeInviteMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeInvite>>,
  TError,
  RevokeInviteMutationVariables,
  TContext
> => {
  return useMutation(getRevokeInviteMutationOptions(options), queryClient);
};
/**
 * @summary Who's inviting you where, and whether the link still works. No sign-in needed.
 */
export const getInvite = (token: string, signal?: AbortSignal) => {
  return customInstance<InvitePreviewDto>({
    url: `/api/v1/invites/${token}`,
    method: 'GET',
    signal,
  });
};

export const getGetInviteQueryKey = (token: string) => {
  return [`/api/v1/invites/${token}`] as const;
};

export const getGetInviteQueryOptions = <
  TData = Awaited<ReturnType<typeof getInvite>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvite>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInviteQueryKey(token);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getInvite>>> = ({ signal }) =>
    getInvite(token, signal);

  return {
    queryKey,
    queryFn,
    enabled: token !== null && token !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getInvite>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetInviteQueryResult = NonNullable<Awaited<ReturnType<typeof getInvite>>>;
export type GetInviteQueryError = ErrorType<ApiErrorDto>;

export function useGetInvite<
  TData = Awaited<ReturnType<typeof getInvite>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvite>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInvite>>,
          TError,
          Awaited<ReturnType<typeof getInvite>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInvite<
  TData = Awaited<ReturnType<typeof getInvite>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvite>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInvite>>,
          TError,
          Awaited<ReturnType<typeof getInvite>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInvite<
  TData = Awaited<ReturnType<typeof getInvite>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Who's inviting you where, and whether the link still works. No sign-in needed.
 */

export function useGetInvite<
  TData = Awaited<ReturnType<typeof getInvite>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetInviteQueryOptions(token, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Join the workspace. Only the invited email address can.
 */
export const acceptInvite = (token: string, signal?: AbortSignal) => {
  return customInstance<AcceptInviteResultDto>({
    url: `/api/v1/invites/${token}/accept`,
    method: 'POST',
    signal,
  });
};

export const getAcceptInviteMutationKey = () => ['acceptInvite'] as const;

export const getAcceptInviteMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof acceptInvite>>,
    TError,
    AcceptInviteMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof acceptInvite>>,
  TError,
  AcceptInviteMutationVariables,
  TContext
> => {
  const mutationKey = getAcceptInviteMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof acceptInvite>>,
    AcceptInviteMutationVariables
  > = (props) => {
    const { token } = props ?? {};

    return acceptInvite(token);
  };

  return { mutationFn, ...mutationOptions };
};

export type AcceptInviteMutationResult = NonNullable<
  Awaited<ReturnType<typeof acceptInvite>>
>;

export type AcceptInviteMutationError = ErrorType<ApiErrorDto>;
export type AcceptInviteMutationVariables = { token: string };

/**
 * @summary Join the workspace. Only the invited email address can.
 */
export const useAcceptInvite = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof acceptInvite>>,
      TError,
      AcceptInviteMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof acceptInvite>>,
  TError,
  AcceptInviteMutationVariables,
  TContext
> => {
  return useMutation(getAcceptInviteMutationOptions(options), queryClient);
};
